import React, { useMemo } from "react";
import { parseISO, format } from "date-fns";
import { useIntl } from "react-intl";
import { RendererProps } from "../shared/renderers";
import {
  asUTC,
  convertUTCToEstablishmentTime,
  DEFAULT_FORMAT_DATE_TIME,
} from "../../lib/dateUtils";
import { DEFAULT_TIME_ZONE } from "../propertyInfo/domain/property.entity";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { StyledIcon } from "../reservationDetail/components/styles";

export function CustomDateTimeRenderer({
  value: valueReq = "",
  config = {},
}: RendererProps<string>) {
  const intl = useIntl();
  const messages = {
    tooltip: {
      id: "customDateTimeRender.tooltip",
      defaultMessage: "Date in timezone: {timeZone}, your local time: {browserDate}",
    },
  };
  const { timeZone = DEFAULT_TIME_ZONE } = config;

  const [establishmentDate, browserDate] = useMemo(() => {
    if (!valueReq) return ["", ""];

    const establishmentFormattedDate = convertUTCToEstablishmentTime(valueReq, timeZone);
    const browserFormattedDate = format(new Date(valueReq), DEFAULT_FORMAT_DATE_TIME);

    return [establishmentFormattedDate, browserFormattedDate];
  }, [valueReq, timeZone]);

  return (
    <>
      {establishmentDate}
      {establishmentDate !== browserDate && (
        <span className="ml-2">
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="error-tooltip">
                {intl.formatMessage(messages.tooltip, {
                  timeZone,
                  browserDate,
                })}
              </Tooltip>
            }>
            <StyledIcon className="fa fa-info-circle" />
          </OverlayTrigger>
        </span>
      )}
    </>
  );
}

export function CustomDateRenderer({ value: valueReq = "" }: RendererProps<string>) {
  const date = asUTC(parseISO(valueReq), "yyyy-MM-dd");
  return <>{date}</>;
}

export function CustomEventTypeRenderer({ value: valueReq = "" }: RendererProps<string>) {
  const intl = useIntl();
  const messages = {
    CREATE: {
      id: "reservationHistory.eventType.create",
      defaultMessage: "CREATE",
    },
    UPDATE: {
      id: "reservationHistory.eventType.update",
      defaultMessage: "UPDATE",
    },
    CANCEL: {
      id: "reservationHistory.eventType.cancel",
      defaultMessage: "CANCEL",
    },
    REACTIVATE: {
      id: "reservationHistory.eventType.reactivate",
      defaultMessage: "REACTIVATE",
    },
  };

  switch (valueReq) {
    case "CREATE":
      return <>{intl.formatMessage(messages.CREATE)}</>;
    case "UPDATE":
      return <>{intl.formatMessage(messages.UPDATE)}</>;
    case "CANCEL":
      return <>{intl.formatMessage(messages.CANCEL)}</>;
    case "REACTIVATE":
      return <>{intl.formatMessage(messages.REACTIVATE)}</>;
    default:
      throw new Error("Invalid event type translation");
  }
}
